import type { ModelState } from '@/model/state';

/** What survives a reload.
 *
 *  A deliberately short list. The chart's own layout — drawings, indicators, the timescale — is persisted by the
 *  charting library itself (see `screens/custom-elements.tsx`), because it owns that state and round-tripping it
 *  through here would be a second, worse copy. What is left is the handful of choices that belong to the app rather
 *  than to the widget, and that a trader would be annoyed to make twice:
 *
 *   · `deskSym`    — the instrument they were last working
 *   · `deskFav`    — their starred instruments
 *   · `deskFavOnly`— whether the desk was filtered to them
 *   · `tf`         — the timeframe the desk was on
 *   · `theme`      — light or dark
 *
 *  Everything else is deliberately *not* persisted. An open position, a half-typed order, a lockout, a drawer left
 *  open: restoring those on a fresh page load would be inventing a session state the server never agreed to, and on
 *  the money path that is the difference between a stale screen and a wrong one.
 */
const PERSISTED = ['deskSym', 'deskFav', 'deskFavOnly', 'tf', 'theme'] as const;
type PersistedKey = (typeof PERSISTED)[number];
export type PersistedState = Pick<ModelState, PersistedKey>;

const KEY = 'pf.workspace.v1';

/** Reads the saved slice, or nothing. Never throws: storage can be unavailable, full, or hold something written by
 *  an older build, and none of those is a reason to fail to start. */
export function loadPersisted(): Partial<PersistedState> {
  try {
    const raw = globalThis.localStorage?.getItem(KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const record = parsed as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    // Key by key, and type-checked: a value of the wrong shape is dropped rather than fed into the model, where it
    // would surface as a rendering error a long way from here.
    for (const key of PERSISTED) {
      const value = record[key];
      if (value === undefined) continue;
      if (key === 'deskFav') {
        if (Array.isArray(value) && value.every((v) => typeof v === 'string')) out[key] = value;
        continue;
      }
      if (key === 'deskFavOnly') {
        if (typeof value === 'boolean') out[key] = value;
        continue;
      }
      if (typeof value === 'string') out[key] = value;
    }
    return out as Partial<PersistedState>;
  } catch {
    return {};
  }
}

export function savePersisted(state: ModelState): void {
  try {
    const slice: Record<string, unknown> = {};
    for (const key of PERSISTED) slice[key] = state[key];
    globalThis.localStorage?.setItem(KEY, JSON.stringify(slice));
  } catch {
    // Quota or private browsing. Losing the preference is a worse next session, not a broken one.
  }
}

/** True when a change touches something worth writing — so a keystroke in the order ticket does not hit storage. */
export function persistedChanged(a: ModelState, b: ModelState): boolean {
  for (const key of PERSISTED) {
    if (key === 'deskFav') {
      const x = a.deskFav;
      const y = b.deskFav;
      if (x.length !== y.length || x.some((v, i) => v !== y[i])) return true;
      continue;
    }
    if (a[key] !== b[key]) return true;
  }
  return false;
}
